// Full subscription status lifecycle, mirrors the backend enum on
// `subscription.status` (see deploy/database/conf/19-subscription-billing-fields.sql
// and docs/design/subscription-plans.md "Subscription status lifecycle").
//
//   active    — normal paying / trialing-with-card customer
//   trialing  — in trial period
//   past_due  — payment failed; dunning in progress
//   read_only — sysadmin or system-imposed write hold
//   suspended — terms-of-service or abuse hold
//   cancelled — customer cancelled, awaiting period_end purge
//   expired   — trial / cancelled past period_end
//   payment_required — trial auto-converted to PAYG without a card on file;
//                      view-only until the $0 PAYG checkout captures one
export type TSubscriptionStatus =
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'read_only'
  | 'suspended'
  | 'cancelled'
  | 'expired'
  | 'payment_required';

// Statuses that the BaseHandler write-gate refuses non-GET requests for.
// Used to render the customer-facing read-only banner and to disable
// "destructive" actions in the UI.
export const WRITE_BLOCKING_STATUSES: TSubscriptionStatus[] = [
  'read_only',
  'suspended',
  'expired',
  'payment_required',
];

export type TSubscriptionPlanDetails = {
  code: string;
  name: string;
  description: string;
  plan_price?: number;
  // The plan's monthly floor on a cycle with billable activity — PAYG's
  // "from $5/mo, fully applied to your usage" (pricing decision 2026-08-12).
  // 0 / absent on every other plan, which is the cue to render nothing.
  //
  // ⚠️ Copy rule, not a preference: this is never presented as a "minimum fee"
  // or "minimum invoice". It is value applied to usage. The blunt mechanic
  // lives in the pricing FAQ only.
  monthly_minimum_usd?: number;
  // Free stored-data allowance, in decimal GB, while the account is DORMANT.
  // null/absent = a paused month is free however much is stored; a number means
  // storage above it is still billed at list even in a month with no activity.
  // The dormant banner has to branch on this, or it tells a customer holding
  // 500 GB that a paused month cost them nothing when it did not.
  dormant_storage_gb?: number | null;
  // How video is billed (conf/22). 'credits': per minute of uploaded video,
  // from the plan's credits — no minutes allowance to show. 'minutes': the
  // transcode-minutes allowance (Custom contracts). Absent on an older
  // backend, which means 'minutes'.
  transcode_billing?: 'credits' | 'minutes';
  data_quota: number;
  api_quota: number;
  video_quota: number;
  user_limit: number;
  max_assets: number;
  ai_credits_initial: number;
  // Per-addon invocation quotas (e.g. {"REMOVE_BACKGROUND": 50, "IMAGE_ANALYSIS": 50}).
  // Drives the per-addon rows in TSubscriptionUsage.addons.
  addon_quotas?: Record<string, number>;
  is_trial: boolean;
  self_serve: boolean;
  display_order: number;
};

export type TSubscriptionGroup = {
  group_id: number;
  name: string;
  member_count: number;
  subscription_id: number;
  created_at: string;
};

export type TSubscriptionUsageMetric = {
  used: number;
  quota: number;
  // Present (true) on fair-use dimensions (assets, odi_transforms):
  // metered internally, never billed; quota is always masked to 0.
  fair_use?: boolean;
};

export type TSubscriptionAddonUsage = {
  code: string;
  used: number;
  quota: number;
};

export type TSubscriptionAiCreditsUsage = {
  used: number;
  quota: number;
  balance: number;
  // Portion of `balance` that came from one-time top-up packs; the remainder
  // (`balance - topup_balance`) is the plan/monthly allowance.
  topup_balance: number;
};

// Seats (plan-structure v2 Phase 6). `used`/`quota` keep the legacy meaning
// (total head-count vs the plan's INCLUDED contributor allowance); the split
// below is what the seats UI reads:
//   contributor = ADMIN / MANAGER / CREATOR, capped at `contributor_quota`
//     (= contributors_included + extra_seats bought at $9/mo via
//     POST /api/v1/billing/seats).
//   viewer      = the read-only USER role, capped at `viewer_quota`.
// 0 quota == unlimited. Optional because responses predating Phase 6 omit it.
export type TSubscriptionSeatUsage = TSubscriptionUsageMetric & {
  contributors?: number;
  contributor_quota?: number;
  contributors_included?: number;
  extra_seats?: number;
  viewers?: number;
  viewer_quota?: number;
  // Non-zero when the viewer cap is DERIVED (migration 53): viewer_quota =
  // viewers_per_seat × (contributors_included + extra_seats). PAYG: 10.
  // 0 / absent = flat plan cap.
  viewers_per_seat?: number;
};

// Lifetime billing rollup. Quotas come from `subscription_plans`; `quota == 0`
// means unlimited. See docs/design/subscription-view-handoff.md.
export type TSubscriptionUsage = {
  storage: TSubscriptionUsageMetric;
  assets: TSubscriptionUsageMetric;
  users: TSubscriptionSeatUsage;
  video_minutes: TSubscriptionUsageMetric;
  // ODI (On-Demand Image) transformations — count of Thumbor requests
  // in the current billing cycle. Drained from Redis every 5 min by
  // `meters.flush_odi_counters` on the backend. See Phase 4 of the
  // billing rebuild plan.
  odi_transforms: TSubscriptionUsageMetric;
  // CDN egress for the current cycle, in RAW BYTES (quota 0 == unlimited).
  // Snapshotted per cycle by meters.poll_cdn_bytes (Cloudflare) or
  // cdn.cdn_daily_traffic_stats (CDN77) on the backend. Optional because
  // responses predating the Phase 5 cutover may omit it.
  cdn?: TSubscriptionUsageMetric;
  // Live-count entitlement caps (rollout 28) — hard block with an upgrade
  // prompt at the cap, never billed. quota 0 == unlimited. Optional because
  // responses predating rollout 28 omit them.
  collections?: TSubscriptionUsageMetric;
  shares?: TSubscriptionUsageMetric;
  addons: TSubscriptionAddonUsage[];
  ai_credits: TSubscriptionAiCreditsUsage;
};

// Phase E usage threshold alert. One entry per dimension that has crossed a
// 75 / 100 / 125 % threshold in the current cycle (highest crossed threshold
// wins). Written server-side by `meters.usage_alerts`; drives the usage banner.
export type TSubscriptionAlert = {
  // 'storage_gb' | 'transcode_minutes' | 'ai_credits'
  dimension: string;
  // 75 | 100 | 125
  threshold_pct: number;
  notified_at: string | null;
};

// "This month would have cost $X on Starter" — the PAYG upgrade nudge.
// Computed server-side from plan_quotas and returned ONLY when a plan is
// genuinely cheaper than the metered total (never a plan that would cost more:
// storage-only usage is often cheaper on PAYG than on any tier). So the UI
// renders it whenever present and never second-guesses the comparison.
export type TUpgradeNudge = {
  plan_code: string;
  plan_name: string;
  // What the cycle's usage costs as metered (PAYG) vs on the suggested plan.
  current_usd: number;
  alternative_usd: number;
  saving_usd: number;
  // The spend level past which the nudge is offered at all (the cheapest paid
  // plan fee). Present for context; the UI doesn't need to re-check it.
  threshold_usd: number;
};

// Live position against the owner's spend limit, computed server-side from
// the same figures the write gate and the invoice use — so the bar, the block
// and the bill cannot disagree.
export type TSpendCapStatus = {
  // The whole cycle bill so far: plan fee + metered usage above allowances.
  spend_usd: number;
  // Just the metered part, excluding the plan fee.
  overage_usd: number;
  // The limit actually enforced (a stored value below the plan's floor is
  // read as the floor).
  cap_usd: number;
  plan_fee_usd: number;
  pct: number;
  // True = writes are being refused RIGHT NOW.
  blocked: boolean;
};

export type TSubscription = {
  id: number;
  owner_user_id: number;
  owner_email: string;
  plan: string;
  // Target of a scheduled (deferred) plan change — applied at the next cycle
  // close. Null when no change is pending.
  pending_plan?: string | null;
  pending_plan_name?: string | null;
  status: TSubscriptionStatus | string;
  // True while the server is refusing writes, for EITHER reason: a blocking
  // subscription status, or the owner's spend limit having been reached. The
  // limit does not show up in `status` (it lifts by itself when the limit is
  // raised or the cycle rolls), so check `spend_cap_status.blocked` before
  // rendering any status-derived copy — otherwise a customer who set their own
  // limit is told to contact support.
  write_blocked?: boolean;
  // Cycle window — added in deploy/database/conf/19-subscription-billing-fields.sql.
  // ISO8601 strings; null when the subscription has not been billed yet
  // (legacy non-enterprise rows, pre-cutover).
  current_period_start?: string | null;
  current_period_end?: string | null;
  trial_ends_at?: string | null;
  cancelled_at?: string | null;
  // DORMANT: an idle cycle on a plan with a monthly minimum billed $0 and put
  // the account to sleep. It shares `status: 'read_only'` with a very different
  // situation — an over-quota or operator-held account — and these two fields
  // are the only way to tell them apart, so branch on `dormant`, never on the
  // status alone. A dormant account holds a card and owes nothing: the copy is
  // "paused, do something to resume", NOT "add a payment method", and the next
  // billable write wakes it server-side with no user action beyond the write.
  dormant?: boolean;
  dormant_since?: string | null;
  // Owner-set ceiling on the cycle's bill — a HARD limit. When the cycle's
  // accrued cost reaches it the server refuses uploads and processing;
  // reads, downloads and CDN delivery are unaffected.
  spend_cap_usd?: number | null;
  // The lowest the limit may be set to on this plan: max(plan fee, monthly
  // minimum). The limit covers the WHOLE bill, so on a paid plan it cannot go
  // below the plan fee. Bound the input with this rather than discovering the
  // floor through a 400.
  spend_cap_minimum_usd?: number | null;
  // Where the open cycle stands against the limit, or null when no limit
  // applies (none set, Enterprise, or a plan with nothing metered) — which is
  // the cue to render the "no limit set" state rather than a 0% bar.
  spend_cap_status?: TSpendCapStatus | null;
  // Free-trial caps reached this cycle (e.g. ["cdn_bytes"]). Non-empty means
  // the server is refusing every change except deletes until a plan is chosen.
  trial_limits_reached?: string[];
  // True once a Stripe Customer exists for the account. The Billing Customer
  // Portal can only be opened for one that does, so a false value means the
  // payment CTA must be "add a card" (checkout), not "manage payment method".
  // Optional: responses predating Phase 7 omit it, and absence is treated as
  // "assume a portal is fine" to preserve the old behaviour.
  has_billing_account?: boolean;
  details: TSubscriptionPlanDetails;
  groups: TSubscriptionGroup[];
  usage: TSubscriptionUsage;
  // Phase E usage threshold alerts for the current cycle (empty when none).
  alerts?: TSubscriptionAlert[];
  // Null unless a cheaper plan genuinely exists for this cycle's usage.
  upgrade_nudge?: TUpgradeNudge | null;
  // Metered charges accrued in the OPEN cycle at list rates, excluding the
  // plan fee — an ESTIMATE (cycle close nets AI drift against top-ups and
  // applies the floor/cap). Null on plans with no metered rate (DEMO,
  // Enterprise) — render nothing then.
  estimated_usage_cost_usd?: number | null;
  created_at: string;
  updated_at: string;
};
